"use client";

import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2",
    "rounded-4",
    "font-bold tracking-[-0.005em]",
    "transition-colors duration-200",
    "focus-visible:outline-none focus-visible:ring-0",
    "whitespace-nowrap cursor-pointer",
  ],
  {
    variants: {
      variant: {
        primary: [
          "bg-primary-700 text-white",
          "hover:bg-primary-500",
          "active:bg-primary-600",
          "aria-selected:bg-primary-600 data-[selected=true]:bg-primary-600",
          "disabled:bg-gray-500 disabled:text-gray-700 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
        secondary: [
          "bg-gray-300 text-gray-900",
          "hover:bg-gray-400",
          "active:bg-gray-500",
          "aria-selected:bg-gray-500 data-[selected=true]:bg-gray-500",
          "disabled:bg-gray-500 disabled:text-gray-700 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
        tertiary: [
          "bg-white text-gray-900",
          "hover:bg-gray-200",
          "active:bg-gray-400",
          "aria-selected:bg-gray-400 data-[selected=true]:bg-gray-400",
          "disabled:bg-white disabled:text-gray-700 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
        outlinedPrimary: [
          "bg-white text-primary-700 border border-primary-700",
          "hover:bg-primary-100",
          "active:bg-primary-200",
          "aria-selected:bg-primary-200 data-[selected=true]:bg-primary-200",
          "disabled:bg-primary-300 disabled:text-gray-700 disabled:border-primary-700 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
        outlinedSecondary: [
          "bg-white text-gray-900 border border-gray-300",
          "hover:bg-gray-200 hover:border-gray-300",
          "active:bg-gray-400 active:border-gray-300",
          "aria-selected:bg-gray-400 aria-selected:border-gray-300 data-[selected=true]:bg-gray-400 data-[selected=true]:border-gray-300",
          "disabled:bg-gray-500 disabled:text-gray-700 disabled:border-gray-300 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
        text: [
          "bg-transparent text-gray-900 font-medium",
          "hover:bg-gray-200",
          "active:bg-gray-400",
          "aria-selected:bg-gray-400 data-[selected=true]:bg-gray-400",
          "disabled:bg-transparent disabled:text-gray-700 disabled:cursor-default disabled:pointer-events-none disabled:opacity-100",
        ],
      },
      size: {
        xsmall: "h-7 min-w-[50px] px-3 py-1.5 text-[13px] leading-[1.46]",
        small: "h-8 min-w-[60px] px-3 py-2 text-sm leading-[1.43]",
        medium: "h-10 min-w-[64px] px-3 py-2 text-[15px] leading-[1.47]",
        large: "h-11 min-w-[78px] px-4 py-3 text-[15px] leading-[1.47]",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "large",
    },
  }
);

const iconSpacingBySize = {
  xsmall: "gap-1",
  small: "gap-1",
  medium: "gap-2",
  large: "gap-2",
} as const;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size = "large",
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          size && iconSpacingBySize[size],
          className
        )}
        ref={ref}
        {...props}
      >
        {leftIcon && <span className="shrink-0 flex items-center">{leftIcon}</span>}
        {children && <span className="flex items-center leading-none">{children}</span>}
        {rightIcon && <span className="shrink-0 flex items-center">{rightIcon}</span>}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
